/* eslint-disable no-param-reassign */
import { createAsyncThunk } from '@reduxjs/toolkit';

import prepareSearchQuery from '../../helpers/assets/prepareSearchQuery';
import { PRODUCTS_EXPORT_LIMIT } from '../../components/ProductsExportCsvDialog/config';
import { isSelectableItem } from './helpers/selectionKey';
import { materializeSelection } from './helpers/applySelection';

// Same ES-window-safe sentinel db.products.search accepts (searchSizeAllAssetsEsLimit).
export const SEARCH_SIZE_ALL_ASSETS_ES_LIMIT = 100000000;

export const selectAllProducts = createAsyncThunk(
  'products/selectAllProducts',
  async (_, { extra: { sdk, Logger }, getState }) => {
    try {
      const query = prepareSearchQuery();
      const { data } = await sdk.products.search({
        ...query,
        onlyIds: true,
        size: SEARCH_SIZE_ALL_ASSETS_ES_LIMIT,
      });

      // onlyIds may omit `type` (ES `_source` often does). The checkbox key is `${type}:${_id}`,
      // so fill type from the already-loaded grid. Untyped leftovers are unknown — not listings
      // — and must not be dropped under `isComplete: true` or Select All exports a subset.
      const loadedById = new Map(
        (getState().products?.search?.items || []).map((item) => [String(item._id), item]),
      );
      const mapped = (data.products || []).map((product) => {
        const loaded = loadedById.get(String(product._id));
        return {
          _id: String(product._id),
          type: product.type || loaded?.type,
        };
      });
      const hasUntyped = mapped.some((product) => !product.type);
      const items = mapped.filter(isSelectableItem);
      if (items.length > PRODUCTS_EXPORT_LIMIT) {
        return { overLimit: true, items: [] };
      }

      return {
        overLimit: false,
        items,
        isComplete: !hasUntyped,
      };
    } catch (error) {
      Logger.error(new Error('Error selecting all products'), { error }, [
        'ProductsSelectAllFailed',
        { errorMessage: error?.message || 'NoMessage' },
      ]);
      throw error;
    }
  },
  {
    condition: (_, { getState }) => !getState().products?.ui?.isSelectingAll,
  },
);

function isCurrentSelectAllRequest(state, requestId) {
  return state.ui.selectAllRequestId === requestId;
}

function settleSelectAll(state) {
  state.ui.isSelectingAll = false;
  state.ui.selectAllRequestId = null;
}

export const reducer = (builder) => {
  builder
    .addCase(selectAllProducts.pending, (state, { meta }) => {
      state.ui.isSelectingAll = true;
      state.ui.selectAllRequestId = meta.requestId;
    })
    .addCase(selectAllProducts.fulfilled, (state, { payload, meta }) => {
      if (!isCurrentSelectAllRequest(state, meta.requestId)) return;
      settleSelectAll(state);
      if (payload.overLimit || !payload.items?.length) return;
      state.ui.selection = materializeSelection(payload.items, {
        isComplete: payload.isComplete !== false,
      });
    })
    .addCase(selectAllProducts.rejected, (state, { meta }) => {
      if (!isCurrentSelectAllRequest(state, meta.requestId)) return;
      settleSelectAll(state);
    });
};
